/**
 * Phase 3 of admin-managed-zebu-accounts: admin subscribes a managed user
 * to a marketplace listing on their behalf. Allocation + subscription rows
 * land under the managed user_id.
 *
 * Opens from the AdminManagedUsersPage per-row "Subscribe to Basket" action.
 * Pre-binds the managed user + their broker client_id; admin picks a listing
 * and lot_multiplier.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import LoadingSpinner from '../common/LoadingSpinner';
import marketplaceService from '../../services/marketplaceService';
import managedMarketplaceService from '../../services/managedMarketplaceService';
import type { MarketplaceListing } from '../../types/marketplace';

interface SubscribeOnBehalfDialogProps {
  open: boolean;
  managedUserId: string;
  managedClientId: string;
  managedDisplayName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const SubscribeOnBehalfDialog: React.FC<SubscribeOnBehalfDialogProps> = ({
  open,
  managedUserId,
  managedClientId,
  managedDisplayName,
  onClose,
  onSuccess,
}) => {
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBasketId, setSelectedBasketId] = useState<string>('');
  const [lotMultiplier, setLotMultiplier] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AMs and Admins both need to see every currently-published listing —
  // not just the caller's own — so they can subscribe managed users to any
  // basket. scope='all' returns the platform-wide set; the backend gates
  // who can call it.
  const fetchListings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await marketplaceService.getAdminListings('all');
      // Only show currently-published listings — unpublished drafts aren't subscribable
      setListings(data.filter((l) => l.is_published));
    } catch (err: any) {
      setError(err.message || 'Failed to load listings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedBasketId('');
      setLotMultiplier(1);
      setError(null);
      fetchListings();
    }
  }, [open, fetchListings]);

  const selectedListing = useMemo(
    () => listings.find((l) => l.basket_id === selectedBasketId),
    [listings, selectedBasketId],
  );

  const handleSubmit = async () => {
    if (!selectedBasketId) {
      setError('Pick a listing');
      return;
    }
    const min = selectedListing?.min_lot_multiplier ?? 1;
    const max = selectedListing?.max_lot_multiplier ?? 250;
    if (lotMultiplier < min || lotMultiplier > max) {
      setError(`Lot multiplier must be between ${min} and ${max}`);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await managedMarketplaceService.subscribeOnBehalf(managedUserId, {
        basket_id: selectedBasketId,
        zebu_client_id: managedClientId,
        lot_multiplier: lotMultiplier,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to subscribe');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen={open} onClose={() => !isSubmitting && onClose()} maxWidth="2xl" ariaLabel="Subscribe Managed User">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Subscribe to Basket</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              On behalf of <span className="font-medium">{managedDisplayName}</span> ({managedClientId})
            </p>
          </div>
          <button onClick={onClose} disabled={isSubmitting} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
            <span className="ml-2 text-gray-600 dark:text-gray-400">Loading listings...</span>
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No published listings available.
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Listing</label>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded p-2">
                {listings.map((listing) => (
                  <label
                    key={listing.basket_id}
                    className={`flex items-start gap-2 p-2 rounded cursor-pointer border ${
                      selectedBasketId === listing.basket_id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <input
                      type="radio"
                      name="listing"
                      value={listing.basket_id}
                      checked={selectedBasketId === listing.basket_id}
                      onChange={() => setSelectedBasketId(listing.basket_id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{listing.title}</span>
                        {listing.published_to_groups && !listing.published_to_groups.includes('public') && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200">
                            {listing.published_to_groups.join(', ')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {listing.category} · {listing.risk_level} · Lots {listing.min_lot_multiplier}-{listing.max_lot_multiplier}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Lot multiplier
                {selectedListing && (
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
                    (min {selectedListing.min_lot_multiplier}, max {selectedListing.max_lot_multiplier})
                  </span>
                )}
              </label>
              <Input
                type="number"
                value={lotMultiplier}
                onChange={(e) => setLotMultiplier(parseInt(e.target.value) || 1)}
                min={selectedListing?.min_lot_multiplier ?? 1}
                max={selectedListing?.max_lot_multiplier ?? 250}
                disabled={isSubmitting || !selectedBasketId}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={isSubmitting || !selectedBasketId}>
            Subscribe
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SubscribeOnBehalfDialog;
